import { useState, useEffect, useRef } from "react";
import type { Habit, HabitId, Category } from "../../../shared/types";
import { useCategories } from "../../../shared/stores/category-store";
import { Overlay } from "../../../ui/atoms/Overlay";

interface HabitEditSheetProps {
  readonly habit: Habit | null;
  readonly onClose: () => void;
  readonly onUpdate: (
    id: HabitId,
    updates: Partial<Pick<Habit, "title" | "category" | "frequency">>,
  ) => void;
  readonly onDelete: (id: HabitId, title: string) => void;
}

export function HabitEditSheet({
  habit,
  onClose,
  onUpdate,
  onDelete,
}: HabitEditSheetProps) {
  return (
    <Overlay open={habit !== null} onClose={onClose}>
      {habit !== null && (
        <HabitEditForm
          key={habit.id}
          habit={habit}
          onClose={onClose}
          onUpdate={onUpdate}
          onDelete={onDelete}
        />
      )}
    </Overlay>
  );
}

interface HabitEditFormProps {
  readonly habit: Habit;
  readonly onClose: () => void;
  readonly onUpdate: (
    id: HabitId,
    updates: Partial<Pick<Habit, "title" | "category" | "frequency">>,
  ) => void;
  readonly onDelete: (id: HabitId, title: string) => void;
}

function HabitEditForm({
  habit,
  onClose,
  onUpdate,
  onDelete,
}: HabitEditFormProps) {
  const { categories, labels } = useCategories();
  const [title, setTitle] = useState(habit.title);
  const [category, setCategory] = useState<Category>(habit.category);
  const [frequency, setFrequency] = useState<"daily" | "weekly">(habit.frequency);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  function handleSave() {
    const trimmed = title.trim();
    if (!trimmed) return;

    const updates: { title?: string; category?: Category; frequency?: "daily" | "weekly" } = {};
    if (trimmed !== habit.title) updates.title = trimmed;
    if (category !== habit.category) updates.category = category;
    if (frequency !== habit.frequency) updates.frequency = frequency;

    if (Object.keys(updates).length > 0) {
      onUpdate(habit.id, updates);
    }
    onClose();
  }

  function handleDelete() {
    onDelete(habit.id, habit.title);
    onClose();
  }

  const hasChanges =
    title.trim() !== habit.title ||
    category !== habit.category ||
    frequency !== habit.frequency;

  return (
    <div className="bg-surface border border-border p-6 w-96 max-w-[90vw] shadow-xl rounded-2xl space-y-5">
      <h2 className="text-lg font-semibold text-ink">Modifier l'habitude</h2>

      <div className="space-y-1">
        <label
          htmlFor="habit-edit-title"
          className="text-xs font-medium text-ink-muted"
        >
          Titre
        </label>
        <input
          ref={titleRef}
          id="habit-edit-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
          }}
          className="w-full px-3 py-2 text-xs text-ink rounded-md border border-border/60 bg-surface-alt focus:outline-none focus:border-accent/60"
        />
      </div>

      <div className="space-y-1">
        <p className="text-xs font-medium text-ink-muted">Catégorie</p>
        <div className="flex gap-1.5 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat as Category)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                category === cat
                  ? "bg-accent text-white"
                  : "bg-surface-alt text-ink-muted border border-border hover:text-ink"
              } focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent`}
            >
              {labels[cat]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-xs font-medium text-ink-muted">Fréquence</p>
        <div className="flex gap-1.5">
          {(["daily", "weekly"] as const).map((freq) => (
            <button
              key={freq}
              type="button"
              onClick={() => setFrequency(freq)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                frequency === freq
                  ? "bg-accent text-white"
                  : "bg-surface-alt text-ink-muted border border-border hover:text-ink"
              } focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-accent`}
            >
              {freq === "daily" ? "Quotidienne" : "Hebdomadaire"}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        {showDeleteConfirm ? (
          <>
            <span className="text-xs text-danger font-medium self-center">Confirmer ?</span>
            <button
              type="button"
              onClick={handleDelete}
              className="px-3 py-2 text-xs font-medium rounded-md bg-danger text-white hover:opacity-80"
            >
              Supprimer
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="px-3 py-2 text-xs font-medium rounded-md border border-border/60 bg-surface text-ink hover:bg-surface-alt"
            >
              Annuler
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3 py-2 text-xs font-medium text-danger hover:bg-danger/10 rounded-md transition-colors"
            >
              Supprimer
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-xs font-medium border border-border/60 bg-surface text-ink hover:bg-surface-alt rounded-md"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!title.trim() || !hasChanges}
              className="px-3 py-2 text-xs font-medium bg-accent text-white hover:opacity-80 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Enregistrer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
